'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import { Collection, Prompt, User } from '@/types';
import PromptGrid from '@/components/prompt/PromptGrid';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/lib/toast';
import { useT } from '@/lib/i18n';
import EditProfileDialog from '@/components/profile/EditProfileDialog';
import ProfileSocials from '@/components/profile/ProfileSocials';
import BotBadge from '@/components/common/BotBadge';
import ActivityHeatmap from '@/components/users/ActivityHeatmap';
import ShareButton from '@/components/ShareButton';

export default function ProfilePage() {
  const { username } = useParams<{ username: string }>();
  const router = useRouter();
  const { user: viewer } = useAuth();
  const toast = useToast();
  const t = useT();
  const [user, setUser] = useState<User | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [activeTag, setActiveTag] = useState<string>('');
  const [editOpen, setEditOpen] = useState(false);
  const [publicCollections, setPublicCollections] = useState<Collection[]>([]);
  // Social graph — hydrated from the profile endpoint + optimistic updates on follow.
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [followHover, setFollowHover] = useState(false);

  useEffect(() => {
    api.users
      .profile(username)
      .then((data) => {
        setUser(data.user);
        setPrompts(data.prompts);
        setIsFollowing(!!data.user.is_following);
        setFollowersCount(data.user.followers_count ?? 0);
        setFollowingCount(data.user.following_count ?? 0);
      })
      .catch(() => setError('User not found.'))
      .finally(() => setLoading(false));
    // Public collections (owner sees private too via the same endpoint)
    api.collections.byUser(username)
      .then(setPublicCollections)
      .catch(() => { /* ignore */ });
  }, [username]);

  const handleFollowToggle = async () => {
    if (!user) return;
    if (!viewer) { router.push('/auth/login'); return; }
    if (followBusy) return;
    const next = !isFollowing;
    setFollowBusy(true);
    // Optimistic flip — roll back on error.
    setIsFollowing(next);
    setFollowersCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      if (next) await api.users.follow(user.username);
      else await api.users.unfollow(user.username);
    } catch {
      setIsFollowing(!next);
      setFollowersCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.error(t('profile.follow_error'));
    } finally {
      setFollowBusy(false);
    }
  };

  // Reset filters when user changes
  useEffect(() => { setActiveCategory(''); setActiveTag(''); }, [username]);

  const stats = useMemo(() => {
    const saves = prompts.reduce((s, p) => s + p.save_count, 0);
    const views = prompts.reduce((s, p) => s + p.view_count, 0);
    const copies = prompts.reduce((s, p) => s + (p.copy_count ?? 0), 0);
    return { count: prompts.length, saves, copies, views };
  }, [prompts]);

  // Derive filter options from this user's prompts
  const categoriesInUse = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of prompts) {
      const c = p.category || 'Other';
      counts[c] = (counts[c] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [prompts]);

  const tagsInUse = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of prompts) {
      for (const t of p.tags) {
        counts[t] = (counts[t] || 0) + 1;
      }
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [prompts]);

  const filtered = useMemo(() => {
    return prompts.filter((p) => {
      if (activeCategory && (p.category || 'Other') !== activeCategory) return false;
      if (activeTag && !p.tags.includes(activeTag)) return false;
      return true;
    });
  }, [prompts, activeCategory, activeTag]);

  const memberSince = useMemo(() => {
    if (!user) return null;
    return new Date(user.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }, [user]);

  const isOwnProfile = viewer?.username === username;
  const hasFilters = !!(activeCategory || activeTag);

  if (loading) {
    return (
      <div>
        <div className="px-6 lg:px-12 py-10 animate-pulse">
          <div className="h-4 w-32 bg-surface-container-high rounded mb-10" />
          <div className="flex flex-col lg:flex-row gap-8 mb-12">
            <div className="w-28 h-28 lg:w-32 lg:h-32 bg-surface-container-high rounded-full" />
            <div className="flex-1 space-y-3">
              <div className="h-4 w-20 bg-surface-container-high rounded" />
              <div className="h-10 w-64 bg-surface-container-high rounded" />
              <div className="h-4 w-40 bg-surface-container-high rounded" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-2xl mb-12 py-6 border-y border-surface-container-high">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-3 w-16 bg-surface-container-high rounded" />
                <div className="h-8 w-20 bg-surface-container-high rounded" />
              </div>
            ))}
          </div>
          <div className="masonry-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="masonry-item">
                <div className="aspect-[4/5] bg-surface-container-high rounded-xl" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div>
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <span className="material-symbols-outlined text-6xl text-outline-variant">person_off</span>
          <h1 className="font-headline text-3xl text-on-surface mt-6 mb-2">{t('profile.user_not_found')}</h1>
          <p className="text-on-surface-variant">{error || t('profile.user_not_found_body')}</p>
          <Link href="/" className="inline-block mt-8 text-primary hover:underline">
            {t('profile.back_to_gallery')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="px-6 lg:px-12 py-4">
        {/* Back */}
        <button onClick={() => router.back()}
          className="inline-flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary transition-colors mb-4">
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_back</span>
          {t('profile.back')}
        </button>
      </div>

      {/* Hero: cover + overlap avatar */}
      <section className="profile-hero">
        <div className="profile-cover">
          {user.cover_image_url && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={user.cover_image_url} alt="" />
          )}
        </div>
        <div className="profile-head-row">
          <div className="profile-avatar-wrap">
            <div className="profile-avatar">
              {user.avatar_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={user.avatar_url} alt={user.name} />
              ) : (
                user.name.slice(0, 1).toUpperCase()
              )}
            </div>
            <div className="profile-identity">
              <div className="eyebrow">
                {user.is_system ? t('profile.eyebrow_system_bot') : (isOwnProfile ? t('profile.eyebrow_own') : t('profile.eyebrow_curator'))}
              </div>
              <h1>
                {user.name}
                {user.is_system && <> <BotBadge size="md" /></>}
              </h1>
              <div className="handle">@{user.username}</div>
              {memberSince && (
                <div className="joined">
                  <span className="material-symbols-outlined" style={{ fontSize: 12 }}>calendar_today</span>
                  {t('profile.joined', { date: memberSince })}
                </div>
              )}
            </div>
          </div>

          <div className="profile-actions">
            {isOwnProfile ? (
              <>
                <button
                  type="button"
                  className="ps-btn ps-btn-secondary ps-btn-sm"
                  onClick={() => setEditOpen(true)}
                >
                  <span className="material-symbols-outlined ps-icon">edit</span>
                  {t('profile.edit_profile')}
                </button>
                <Link href="/create" className="ps-btn ps-btn-primary ps-btn-sm no-underline">
                  <span className="material-symbols-outlined ps-icon">add</span>
                  {t('profile.new_prompt')}
                </Link>
              </>
            ) : viewer ? (
              <button
                type="button"
                className={isFollowing ? 'ps-btn ps-btn-secondary ps-btn-sm' : 'ps-btn ps-btn-primary ps-btn-sm'}
                onClick={handleFollowToggle}
                onMouseEnter={() => setFollowHover(true)}
                onMouseLeave={() => setFollowHover(false)}
                disabled={followBusy}
                aria-busy={followBusy || undefined}
                aria-label={isFollowing ? t('profile.unfollow') : t('profile.follow')}
              >
                <span className="material-symbols-outlined ps-icon">
                  {isFollowing ? (followHover ? 'person_remove' : 'check') : 'person_add'}
                </span>
                {followBusy
                  ? (isFollowing ? t('profile.unfollowing') : t('profile.following_progress'))
                  : (isFollowing ? (followHover ? t('profile.unfollow') : t('profile.following')) : t('profile.follow'))}
              </button>
            ) : null}
            {/* Share profile link — works for anyone visiting, even logged-out viewers.
                Facebook + X pick up the ProfilePage OG metadata we ship server-side. */}
            <ShareButton
              url={`/profile/${user.username}`}
              title={`${user.name} (@${user.username}) on Prompt Save`}
              iconOnly
            />
          </div>
        </div>

        {/* Bio */}
        {user.bio && <p className="profile-bio">{user.bio}</p>}

        {/* Socials */}
        {user.social_links && Object.keys(user.social_links).length > 0 && (
          <ProfileSocials links={user.social_links} />
        )}

        {/* Stats — integrated into the hero so it aligns with name/bio.
            Follower/following counts link to the full list pages. */}
        <div className="profile-stats">
          <Link href={`/profile/${user.username}/followers`} className="profile-stat profile-stat-linked no-underline">
            <span className="profile-stat-label">{t('profile.stat_followers')}</span>
            <span className="profile-stat-value">{followersCount.toLocaleString()}</span>
          </Link>
          <Link href={`/profile/${user.username}/following`} className="profile-stat profile-stat-linked no-underline">
            <span className="profile-stat-label">{t('profile.stat_following')}</span>
            <span className="profile-stat-value">{followingCount.toLocaleString()}</span>
          </Link>
          {[
            { label: t('profile.stat_prompts'),      value: stats.count  },
            { label: t('profile.stat_total_saves'),  value: stats.saves  },
            { label: t('profile.stat_total_copies'), value: stats.copies },
            { label: t('profile.stat_total_views'),  value: stats.views  },
          ].map((s) => (
            <div key={s.label} className="profile-stat">
              <span className="profile-stat-label">{s.label}</span>
              <span className="profile-stat-value">{s.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="px-6 lg:px-12 py-10">

        {/* Activity heatmap — always rendered (fetches its own data). */}
        <ActivityHeatmap username={user.username} />

        {/* Public collections (or all if viewing own profile) */}
        {publicCollections.length > 0 && (
          <section style={{ marginBottom: 48 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 4 }}>
                  {isOwnProfile ? t('profile.your_library') : t('profile.curated')}
                </div>
                <h2 style={{
                  fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 600,
                  letterSpacing: '-0.02em', color: 'var(--ink)', margin: 0,
                }}>
                  {isOwnProfile ? t('profile.your_collections') : t('profile.collections')}
                </h2>
              </div>
              {isOwnProfile && (
                <Link href="/my/saved" className="no-underline" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  {t('profile.manage')}
                </Link>
              )}
            </div>
            <div className="saved-collections">
              {publicCollections.map((c) => (
                <PublicCollectionTile key={c.id} collection={c} />
              ))}
            </div>
          </section>
        )}

        {/* Collection heading */}
        <div className="mb-6">
          <span className="font-label text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-bold block mb-2">
            {t('profile.section_eyebrow')}
          </span>
          <h2 className="font-headline text-3xl text-on-surface">
            {stats.count === 0
              ? t('profile.empty_no_public')
              : isOwnProfile
                ? t('profile.your_published_prompts')
                : t('profile.published_by', { name: user.name.split(' ')[0] })}
          </h2>
        </div>

        {/* Filters */}
        {stats.count > 0 && (
          <div className="mb-8 space-y-4">
            {/* Type (category) row */}
            {categoriesInUse.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-label text-[10px] uppercase tracking-widest text-outline mr-1 w-12 shrink-0">{t('profile.type_label')}</span>
                <button
                  onClick={() => setActiveCategory('')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    activeCategory === ''
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface-container-lowest text-on-surface-variant border-outline-variant hover:border-primary hover:text-primary'
                  }`}
                >
                  {t('profile.all')} <span className="opacity-60">· {prompts.length}</span>
                </button>
                {categoriesInUse.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => setActiveCategory(activeCategory === c.name ? '' : c.name)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      activeCategory === c.name
                        ? 'bg-primary text-white border-primary'
                        : 'bg-surface-container-lowest text-on-surface-variant border-outline-variant hover:border-primary hover:text-primary'
                    }`}
                  >
                    {c.name} <span className="opacity-60">· {c.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Tag row */}
            {tagsInUse.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-label text-[10px] uppercase tracking-widest text-outline mr-1 w-12 shrink-0">{t('profile.tag_label')}</span>
                <button
                  onClick={() => setActiveTag('')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    activeTag === ''
                      ? 'bg-primary-fixed text-primary ring-2 ring-primary/30'
                      : 'bg-surface-container-low text-on-surface-variant hover:bg-primary-fixed/60 hover:text-primary'
                  }`}
                >
                  {t('profile.all')}
                </button>
                {tagsInUse.map((tg) => (
                  <button
                    key={tg.name}
                    onClick={() => setActiveTag(activeTag === tg.name ? '' : tg.name)}
                    className={`inline-flex items-baseline gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      activeTag === tg.name
                        ? 'bg-primary-fixed text-primary ring-2 ring-primary/30'
                        : 'bg-surface-container-low text-on-surface-variant hover:bg-primary-fixed/60 hover:text-primary'
                    }`}
                    title={t(tg.count === 1 ? 'profile.tag_title_one' : 'profile.tag_title_other', { count: tg.count, name: tg.name })}
                  >
                    <span className="font-headline italic">#</span>{tg.name}
                    <span className="opacity-60 ml-0.5">{tg.count}</span>
                  </button>
                ))}
              </div>
            )}

            {hasFilters && (
              <div className="flex items-center gap-3 pt-1">
                <span className="text-xs text-on-surface-variant">
                  {t('profile.showing', { count: filtered.length, total: prompts.length })}
                </span>
                <button onClick={() => { setActiveCategory(''); setActiveTag(''); }}
                  className="text-xs text-on-surface-variant hover:text-primary inline-flex items-center gap-1">
                  <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>close</span>
                  {t('profile.clear_filters')}
                </button>
              </div>
            )}
          </div>
        )}

        <PromptGrid
          prompts={filtered}
          emptyMessage={
            hasFilters
              ? t('profile.empty_filters')
              : isOwnProfile
                ? t('profile.empty_publish_first')
                : t('profile.empty_user_no_public', { name: user.name })
          }
        />
      </div>

      {editOpen && isOwnProfile && (
        <EditProfileDialog
          user={user}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => { setUser(updated); setEditOpen(false); }}
        />
      )}
    </div>
  );
}

function PublicCollectionTile({ collection }: { collection: Collection }) {
  const t = useT();
  const hue = collection.hue ?? '200';
  return (
    <Link href={`/collection/${collection.id}`} className="no-underline saved-coll">
      {!collection.is_public && (
        <span
          title={t('profile.private')}
          style={{
            position: 'absolute', top: 14, right: 14,
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 10, color: 'var(--ink-3)', fontWeight: 500,
            letterSpacing: '0.04em', textTransform: 'uppercase',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 12 }}>lock</span>
          {t('profile.private')}
        </span>
      )}
      <div
        className="saved-coll-icon"
        style={{ background: `oklch(0.94 0.04 ${hue})`, color: `oklch(0.35 0.14 ${hue})` }}
      >
        <span className="material-symbols-outlined">{collection.icon ?? 'folder'}</span>
      </div>
      <div className="saved-coll-stack">
        {collection.preview.slice(0, 3).map((p, i) => (
          <div
            key={p.id}
            className="saved-coll-mini"
            style={{
              transform: `translateX(${i * 10}px) rotate(${(i - 1) * 3}deg)`,
              zIndex: 3 - i,
            }}
          >
            {p.cover_image_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={p.cover_image_url} alt="" />
            ) : (
              <div className="saved-coll-mini-text">{(p.title || p.category || '?').slice(0, 1).toUpperCase()}</div>
            )}
          </div>
        ))}
      </div>
      <div className="saved-coll-name">{collection.name}</div>
      {collection.description && <div className="saved-coll-desc">{collection.description}</div>}
      <div className="saved-coll-count">
        {t(collection.item_count === 1 ? 'profile.private_collection_count_one' : 'profile.private_collection_count_other', { count: collection.item_count })}
      </div>
    </Link>
  );
}
